// Dashboard API service
import { baseClient } from './baseClient';

export interface DashboardData {
  summary: {
    totalDeals: number;
    totalContacts: number;
    totalAccounts: number;
    totalTasks: number;
    pipelineValue: number;
    wonDeals: number;
    lostDeals: number;
  };
  charts: {
    dealsByStage: Array<{ stage: string; count: number; value: number }>;
    dealsByOwner: Array<{ owner: string; count: number; value: number }>;
    dealsByMonth: Array<{ month: string; count: number; value: number }>;
    activitiesByType: Array<{ type: string; count: number }>;
  };
  recentActivity: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
    entityId?: string;
    entityType?: string;
  }>;
  insights: Array<{
    id: string;
    text: string;
    type: string;
    priority: 'high' | 'medium' | 'low';
    timestamp: string;
  }>;
  tasks: Array<{
    id: string;
    description: string;
    dueDate: string;
    isCompleted: boolean;
    dealId?: string;
  }>;
}

export class DashboardApi {
  private static instance: DashboardApi;

  private constructor() { }

  public static getInstance(): DashboardApi {
    if (!DashboardApi.instance) {
      DashboardApi.instance = new DashboardApi();
    }
    return DashboardApi.instance;
  }

  /**
   * Get main dashboard data
   */
  async getDashboardData(): Promise<DashboardData> {
    return baseClient.get<DashboardData>('/dashboard/');
  }

  /**
   * Get KPIs for executive dashboard
   */
  async getKPIs(): Promise<any> {
    return baseClient.get<any>('/dashboard/kpis');
  }

  /**
   * Get dashboard summary
   */
  async getDashboardSummary(): Promise<DashboardData['summary']> {
    return baseClient.get<DashboardData['summary']>('/dashboard/summary');
  }

  /**
   * Get deals by stage chart data
   */
  async getDealsByStage(): Promise<DashboardData['charts']['dealsByStage']> {
    return baseClient.get<DashboardData['charts']['dealsByStage']>('/dashboard/charts/deals-by-stage');
  }

  /**
   * Get deals by owner chart data
   */
  async getDealsByOwner(): Promise<DashboardData['charts']['dealsByOwner']> {
    return baseClient.get<DashboardData['charts']['dealsByOwner']>('/dashboard/charts/deals-by-owner');
  }

  /**
   * Get deals by month chart data
   */
  async getDealsByMonth(): Promise<DashboardData['charts']['dealsByMonth']> {
    return baseClient.get<DashboardData['charts']['dealsByMonth']>('/dashboard/charts/deals-by-month');
  }

  /**
   * Get activities by type chart data
   */
  async getActivitiesByType(): Promise<DashboardData['charts']['activitiesByType']> {
    return baseClient.get<DashboardData['charts']['activitiesByType']>('/dashboard/charts/activities-by-type');
  }

  /**
   * Get recent activity
   */
  async getRecentActivity(limit: number = 10): Promise<DashboardData['recentActivity']> {
    return baseClient.get<DashboardData['recentActivity']>(`/dashboard/recent-activity?limit=${limit}`);
  }

  /**
   * Get dashboard insights
   */
  async getDashboardInsights(limit: number = 5): Promise<DashboardData['insights']> {
    return baseClient.get<DashboardData['insights']>(`/dashboard/insights?limit=${limit}`);
  }

  /**
   * Get dashboard tasks
   */
  async getDashboardTasks(limit: number = 10): Promise<DashboardData['tasks']> {
    return baseClient.get<DashboardData['tasks']>(`/dashboard/tasks?limit=${limit}`);
  }

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics(): Promise<any> {
    return baseClient.get<any>('/dashboard/performance');
  }

  /**
   * Get sales pipeline data
   */
  async getSalesPipeline(): Promise<any> {
    return baseClient.get<any>('/dashboard/sales-pipeline');
  }

  /**
   * Get conversion rates
   */
  async getConversionRates(): Promise<any> {
    return baseClient.get<any>('/dashboard/conversion-rates');
  }

  /**
   * Get revenue trends
   */
  async getRevenueTrends(period: string = 'monthly'): Promise<any> {
    return baseClient.get<any>(`/dashboard/revenue-trends?period=${period}`);
  }

  /**
   * Get activity trends
   */
  async getActivityTrends(period: string = 'weekly'): Promise<any> {
    return baseClient.get<any>(`/dashboard/activity-trends?period=${period}`);
  }

  /**
   * Get top performing deals
   */
  async getTopPerformingDeals(limit: number = 10): Promise<any[]> {
    return baseClient.get<any[]>(`/dashboard/top-deals?limit=${limit}`);
  }

  /**
   * Get top performing accounts
   */
  async getTopPerformingAccounts(limit: number = 10): Promise<any[]> {
    return baseClient.get<any[]>(`/dashboard/top-accounts?limit=${limit}`);
  }

  /**
   * Get overdue tasks
   */
  async getOverdueTasks(): Promise<any[]> {
    return baseClient.get<any[]>('/dashboard/overdue-tasks');
  }

  /**
   * Get upcoming deadlines
   */
  async getUpcomingDeadlines(days: number = 7): Promise<any[]> {
    return baseClient.get<any[]>(`/dashboard/upcoming-deadlines?days=${days}`);
  }

  /**
   * Get alerts and notifications
   */
  async getAlerts(): Promise<any[]> {
    return baseClient.get<any[]>('/dashboard/alerts');
  }

  /**
   * Get dashboard widgets configuration
   */
  async getWidgetsConfig(): Promise<any> {
    return baseClient.get<any>('/dashboard/widgets-config');
  }

  /**
   * Update dashboard widgets configuration
   */
  async updateWidgetsConfig(config: any): Promise<any> {
    return baseClient.put<any>('/dashboard/widgets-config', config);
  }

  /**
   * Get custom dashboard data
   */
  async getCustomDashboard(widgetIds: string[]): Promise<any> {
    const params = new URLSearchParams();
    widgetIds.forEach(id => params.append('widgets', id));

    return baseClient.get<any>(`/dashboard/custom?${params.toString()}`);
  }

  /**
   * Export dashboard data
   */
  async exportDashboardData(format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    const response = await baseClient.get<Blob>(`/dashboard/export?format=${format}`, {
      responseType: 'blob'
    });
    return response;
  }

  /**
   * Get dashboard analytics
   */
  async getDashboardAnalytics(): Promise<any> {
    return baseClient.get<any>('/dashboard/analytics');
  }

  /**
   * Get real-time dashboard updates
   */
  async getRealTimeUpdates(): Promise<any> {
    return baseClient.get<any>('/dashboard/real-time');
  }
}

// Export singleton instance
export const dashboardApi = DashboardApi.getInstance(); 